import type { FloorDef } from '../types';
import { room, door, stairs } from '../dsl';

/**
 * 3층 — 낡고 좁은 하인 구역. 긴 복도(corr3)가 층을 가로지르고 양쪽에 작은 방들이 붙어 있다.
 * 북쪽: 관측실·기록실·하인 방·병실(감옥 후보)·벽장(옷장 뒤 비밀문 → 사다리 방 → 다락)·린넨실·그림 창고.
 * 남쪽: 낡은 육아실·하인 방·인형 방·계단참(2층으로)·시계 방·빈 방·하인 계단실(2층 하인 계단으로).
 * Grid 128 x 64.
 */
export const THIRD: FloorDef = {
  id: 'third',
  w: 128,
  h: 64,
  rooms: [
    // 복도
    room('corr3', '3층 복도', 12, 30, 104, 4, 'corridor', 'plank', 'plaster'),

    // 북쪽 줄 (바닥 끝 y=28, 복도 북벽 y=29)
    room('observatory', '관측실', 12, 17, 18, 12, 'observatory', 'wood', 'cream', { windows: 0.6 }),
    room('archive', '기록실', 31, 19, 16, 10, 'archive', 'wood', 'cream', { windows: 0.3 }),
    room('servant1', '하인 방 1', 48, 24, 6, 5, 'servant', 'plank', 'plaster', { windows: 0.3 }),
    room('servant2', '하인 방 2', 55, 24, 6, 5, 'servant', 'plank', 'plaster', { windows: 0.3 }),
    room('sick', '병실', 62, 19, 16, 10, 'sick', 'plank', 'plaster', { prison: true, windows: 0.3 }),
    room('closet', '벽장', 79, 24, 7, 5, 'closet', 'plank', 'plaster', {
      furnish: false,
      props: [
        // 비밀문(81,23) 바로 아래. 좌(79,80)·우(83..85)에 밀 공간을 비워 둔다.
        // Earlier saves placed it at 82 after doorway cleanup; keep that origin
        // when restoring those runs, without moving furniture under a character.
        { kind: 'wardrobePush', id: 'attic-wardrobe', x: 81, y: 24, data: { trigger: 'closet_pushed', dist: 2, legacyOriginX: 82 } },
        { kind: 'shelf', x: 79, y: 27 },
        { kind: 'crate', x: 85, y: 28 },
      ],
    }),
    room('atticnook', '사다리 방', 80, 20, 3, 3, 'empty', 'plank', 'plaster'),
    room('linen3', '린넨실', 87, 22, 12, 7, 'linen', 'plank', 'plaster'),
    room('storeroom3', '그림 창고', 100, 21, 16, 8, 'storage', 'plank', 'plaster', { windows: 0.3 }),

    // 남쪽 줄 (바닥 시작 y=35, 복도 남벽 y=34)
    room('nursery2', '낡은 육아실', 12, 35, 16, 11, 'nursery', 'wood', 'cream'),
    room('servant3', '하인 방 3', 29, 35, 6, 5, 'servant', 'plank', 'plaster'),
    room('servant4', '하인 방 4', 36, 35, 6, 5, 'servant', 'plank', 'plaster'),
    room('dollroom', '인형 방', 43, 35, 14, 10, 'doll', 'wood', 'cream'),
    room('landing3', '3층 계단참', 58, 35, 14, 10, 'stairhall', 'wood', 'cream'),
    room('clockroom', '시계 방', 73, 35, 14, 10, 'clock', 'wood', 'cream'),
    room('emptyroom', '빈 방', 88, 35, 14, 8, 'empty', 'plank', 'plaster'),
    room('servstair3', '하인 계단실', 103, 35, 13, 10, 'stairhall', 'plank', 'plaster'),
  ],
  doors: [
    // 복도 ↔ 북쪽 줄
    door('corr3', 'observatory', 'door'),
    door('corr3', 'archive', 'door'),
    door('corr3', 'servant1', 'door'),
    door('corr3', 'servant2', 'door'),
    door('corr3', 'sick', 'bolt', { id: 'sick-bolt', label: '병실 문' }),
    door('corr3', 'closet', 'door'),
    door('corr3', 'linen3', 'door'),
    door('corr3', 'storeroom3', 'door'),
    // 복도 ↔ 남쪽 줄
    door('corr3', 'nursery2', 'door'),
    door('corr3', 'servant3', 'door'),
    door('corr3', 'servant4', 'door'),
    door('corr3', 'dollroom', 'door'),
    door('corr3', 'landing3', 'door'),
    door('corr3', 'clockroom', 'door'),
    door('corr3', 'emptyroom', 'door'),
    door('corr3', 'servstair3', 'door'),
    // 방끼리 (우회로)
    door('observatory', 'archive', 'door', { at: 0.5 }),
    door('servant1', 'servant2', 'door', { at: 0.5 }),
    door('nursery2', 'servant3', 'door', { at: 0.5 }),
    door('servant3', 'servant4', 'door', { at: 0.5 }),
    door('dollroom', 'landing3', 'door', { at: 0.5 }),
    door('clockroom', 'emptyroom', 'door', { at: 0.5 }),
    door('emptyroom', 'servstair3', 'door', { at: 0.5 }),
    door('linen3', 'storeroom3', 'door', { at: 0.5 }),
    // 벽장 → 사다리 방 (옷장을 2칸 밀면 열림)
    door('closet', 'atticnook', 'secret', { id: 'closet-secret', trigger: 'closet_pushed', at: 0.5 }),
  ],
  stairs: [
    stairs('third3', 'landing3', 64, 42, 'down', 'N', 'second', 'third2'),
    stairs('serv3', 'servstair3', 108, 42, 'down', 'N', 'second', 'serv2u'),
    stairs('attic3', 'atticnook', 81, 20, 'up', 'S', 'attic', 'attic_l', { kind: 'ladder' }),
  ],
};
